import axios from 'axios';
import { pickBy } from 'lodash';
import moment from 'moment';
import {
  apiInstancesSurveysResponses,
  apiInstancesSurveysResponsesForInstanceLinkUuidUrl
} from '../config';
import populatePropertys from '../utils/populatePropertys';

/**
 * GET Instances Responses
 *
 * @author Yves Matta <yves.matta@qochealth.com>
 * @param {{}} props - properties Object
 * @param {{}} [props.requestConfig] - additional config for request
 * @param {boolean} [props.beforeThreshold] - return results before threshold (default is after)
 * @param {boolean} [props.completionQuestion] - target completion question responses
 * @param {string} [props.deleted=exclude] - include | exclude | only
 * @param {string} [props.displaySetId] - target displaySetId[s]
 * @param {string[]} [props.displaySetIds] - target displaySetIds
 * @param {string} [props.instanceLinkUUID] - target instanceLinkUUID[s]
 * @param {string[]} [props.instanceLinkUUIDs] - array of target instanceLinkUUIDs
 * @param {string} [props.instanceUUID] - target instanceUUID[s]
 * @param {string[]} [props.instanceUUIDs] - array of target instanceUUIDs
 * @param {string} [props.linkedProfileUUID] - target linkedProfileUUID
 * @param {number} [props.maxResults] - max records to be returned
 * @param {boolean} [props.messagesEnabled] - only return results where instanceLink messagesEnabled true
 * @param {boolean} [props.noCollapse] - return full results (don't worry about most recent)
 * @param {string} [props.orgGroupUUID] - target orgGroupUUID
 * @param {string} [props.orgLocationUUID] - target orgLocationUUID
 * @param {string} [props.orgUnitUUID] - target orgUnitUUID
 * @param {string} [props.orgUUID] - target orgUUID
 * @param {number} [props.pageSize] - target pageSize
 * @param {number} [props.pageNum] - target pageNum
 * @param {string} [props.profileUUID] - target profileUUID
 * @param {string} [props.questionId] - target questionId[s]
 * @param {string[]} [props.questionIds] - target questionIds
 * @param {string} [props.questionSetId] - target questionSetId[s]
 * @param {string[]} [props.questionSetIds] - target questionSetIds
 * @param {string} [props.responseDataType] - target responseDataType
 * @param {number} [props.responseSetDateTime] - target responseSetDateTime
 * @param {string} [props.responseSetId] - target responseSetId
 * @param {string} [props.responseTypeClass] - target responseTypeClass
 * @param {string} [props.searchGeoLatitude] - Search Geo Latitude (https://en.wikipedia.org/wiki/Decimal_degrees)
 * @param {string} [props.searchGeoLongitude] - Search Geo Longitude (https://en.wikipedia.org/wiki/Decimal_degrees)
 * @param {number} [props.searchGeoRadius] - Search within a specific Geo Radius
 * @param {string} [props.searchGeoRadiusUnits] - Units for Geo Search Radius (default: KM)
 * @param {string} [props.searchQuestionId] - target questionId[s]
 * @param {string[]} [props.searchQuestionIds] - target questionIds
 * @param {string} [props.searchQuestionSetId] - target questionSetId[s]
 * @param {string[]} [props.searchQuestionSetIds] - target questionSetIds
 * @param {string} [props.searchString] - Search String
 * @param {string} [props.surveyLinkUUID] - target surveyLinkUUID
 * @param {string} [props.thresholdDateTime] - target threshold datetime
 * @returns {Promise}
 */
const getInstancesResponses = (props = {}) => {
  const {
    deleted = 'exclude',
    displaySetId,
    displaySetIds,
    instanceLinkUUID,
    instanceLinkUUIDs,
    instanceUUID,
    instanceUUIDs,
    requestConfig,
    questionId,
    questionIds,
    questionSetId,
    questionSetIds,
    responseSetDateTime,
    searchQuestionId,
    searchQuestionIds,
    searchQuestionSetId,
    searchQuestionSetIds,
    thresholdDateTime
  } = props;

  const method = 'get';
  let url = apiInstancesSurveysResponses;

  if (instanceLinkUUID) {
    url = apiInstancesSurveysResponsesForInstanceLinkUuidUrl.replace(
      /{instanceLinkUUID}/g,
      instanceLinkUUID
    );
  }

  const populatedPropertys = populatePropertys({
    capDestProp: true,
    propertyNames: [
      'beforeThreshold',
      'completionQuestion',
      'displaySetId',
      'linkedProfileUUID',
      'maxResults',
      'messagesEnabled',
      'noCollapse',
      'orgGroupUUID',
      'orgLocationUUID',
      'orgUnitUUID',
      'orgUUID',
      'pageSize',
      'pageNum',
      'profileUUID',
      'searchGeoLatitude',
      'searchGeoLongitude',
      'searchGeoRadius',
      'searchGeoRadiusUnits',
      'searchString',
      'surveyLinkUUID'
    ],
    source: props
  });

  const params = pickBy(
    {
      Deleted: deleted,
      'DisplaySetIds[]': displaySetId ? undefined : displaySetIds,
      'InstanceUUIDs[]': instanceUUID ? undefined : instanceUUIDs,
      'InstanceLinkUUIDs[]': instanceLinkUUID ? undefined : instanceLinkUUIDs,
      'QuestionIds[]': questionId ? undefined : questionIds,
      'QuestionSetIds[]': questionSetId ? undefined : questionSetIds,
      ResponseSetDateTime: responseSetDateTime
        ? moment(responseSetDateTime).valueOf()
        : undefined,
      'SearchQuestionIds[]': searchQuestionId ? undefined : searchQuestionIds,
      'SearchQuestionSetIds[]': searchQuestionSetId
        ? undefined
        : searchQuestionSetIds,
      ThresholdDateTime: thresholdDateTime
        ? moment(thresholdDateTime).valueOf()
        : undefined,
      ...populatedPropertys
    },
    property => typeof property !== 'undefined'
  );

  return axios({
    method,
    params,
    url,
    ...requestConfig
  });
};

export default getInstancesResponses;



// WEBPACK FOOTER //
// ./src/qoc-sdk/library/getInstancesResponses.js